/**
 * Web browser implementation of secure storage using localStorage with encryption
 */

import type { SecureStorageInterface } from './SecureStorageInterface'
import { EncryptionUtils } from './SecureStorageInterface'
import { storageLogger } from '@/lib/logger'

export class LocalSecureStorage implements SecureStorageInterface {
  private encryptionKey: CryptoKey | null = null
  private readonly prefix = 'hue_secure_'

  private async getEncryptionKey(): Promise<CryptoKey> {
    if (!this.encryptionKey) {
      this.encryptionKey = await EncryptionUtils.getOrCreateEncryptionKey()
    }
    return this.encryptionKey
  }

  async setItem(key: string, value: unknown): Promise<void> {
    try {
      const encryptionKey = await this.getEncryptionKey()
      const serialized = JSON.stringify(value)
      const encrypted = await EncryptionUtils.encrypt(serialized, encryptionKey)
      localStorage.setItem(this.prefix + key, encrypted)
    } catch (error) {
      storageLogger.error('Failed to store encrypted item:', error)
      throw new Error('Failed to securely store data')
    }
  }

  async getItem(key: string): Promise<unknown | null> {
    try {
      const encrypted = localStorage.getItem(this.prefix + key)
      if (!encrypted) return null

      const encryptionKey = await this.getEncryptionKey()
      const decrypted = await EncryptionUtils.decrypt(encrypted, encryptionKey)
      return JSON.parse(decrypted)
    } catch (error) {
      storageLogger.error('Failed to retrieve encrypted item:', error)
      return null
    }
  }

  async removeItem(key: string): Promise<void> {
    localStorage.removeItem(this.prefix + key)
  }

  async clear(): Promise<void> {
    const keys = Object.keys(localStorage)
    for (const key of keys) {
      if (key.startsWith(this.prefix)) {
        localStorage.removeItem(key)
      }
    }
  }

  /**
   * Get all stored keys (without prefix)
   */
  async getAllKeys(): Promise<string[]> {
    const keys = Object.keys(localStorage)
    return keys
      .filter(key => key.startsWith(this.prefix))
      .map(key => key.substring(this.prefix.length))
  }
}